<template>
  <div
    v-if="result"
    class="exercise-result-wrap"
  >
    <div class="exercise-result-hero">
      <div class="exercise-result-tags">
        <span
          v-if="result.formatLabel"
          class="result-tag"
        >{{ result.formatLabel }}</span>
        <span
          v-if="result.difficultyLabel"
          class="result-tag"
        >{{ result.difficultyLabel }}</span>
        <span
          v-if="result.sourceLabel"
          class="result-tag muted"
        >{{ result.sourceLabel }}</span>
      </div>
      <div
        v-if="result.intro"
        class="exercise-result-intro"
        v-html="latex(result.intro)"
      />
    </div>

    <div class="exercise-result-list">
      <article
        v-for="(item, index) in items"
        :key="item.id || index"
        class="exercise-result-item"
      >
        <div class="exercise-result-item-head">
          <span class="exercise-item-index">Exercice {{ index + 1 }}</span>
          <h5 v-html="latex(item.title || result.formatLabel || 'Exercice')" />
        </div>

        <div
          v-if="item.context"
          class="exercise-item-context"
          v-html="latex(item.context)"
        />

        <div class="exercise-item-block">
          <span class="exercise-item-label">Consigne</span>
          <div
            class="exercise-item-text"
            v-html="latex(item.prompt)"
          />
        </div>

        <div
          v-if="item.steps && item.steps.length"
          class="exercise-item-block"
        >
          <span class="exercise-item-label">Etapes</span>
          <ol class="exercise-item-list ordered">
            <li
              v-for="(step, stepIndex) in item.steps"
              :key="'step-' + stepIndex"
              v-html="latex(step)"
            />
          </ol>
        </div>

        <div
          v-if="item.hints && item.hints.length"
          class="exercise-item-block"
        >
          <span class="exercise-item-label">Indices</span>
          <ul class="exercise-item-list">
            <li
              v-for="(hint, hintIndex) in item.hints"
              :key="'hint-' + hintIndex"
              v-html="latex(hint)"
            />
          </ul>
        </div>

        <div
          v-if="item.rubric && item.rubric.length"
          class="exercise-item-block"
        >
          <span class="exercise-item-label">Points a verifier</span>
          <ul class="exercise-item-list">
            <li
              v-for="(point, pointIndex) in item.rubric"
              :key="'rubric-' + pointIndex"
              v-html="latex(point)"
            />
          </ul>
        </div>

        <div
          v-if="item.expectedAnswer"
          class="exercise-item-block correction-wrapper"
        >
          <button
            type="button"
            class="correction-toggle"
            @click="toggleCorrection(index)"
          >
            <span class="correction-toggle-icon">{{ isCorrectionOpen(index) ? '&#9660;' : '&#9654;' }}</span>
            <span>{{ isCorrectionOpen(index) ? 'Masquer la correction' : 'Voir la correction' }}</span>
          </button>
          <transition name="correction-slide">
            <div
              v-if="isCorrectionOpen(index)"
              class="correction-body"
            >
              <div
                class="exercise-item-text"
                v-html="latex(item.expectedAnswer)"
              />
            </div>
          </transition>
        </div>
      </article>
    </div>
  </div>
</template>

<script>
import { renderLatex } from '@/libs/katexRender'

export default {
  name: 'ExerciseResultContent',
  props: {
    result: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      openCorrections: {},
    }
  },
  computed: {
    items() {
      return this.result && Array.isArray(this.result.items) ? this.result.items : []
    },
  },
  methods: {
    latex(text) {
      return renderLatex(text)
    },
    toggleCorrection(index) {
      this.$set(this.openCorrections, index, !this.openCorrections[index])
    },
    isCorrectionOpen(index) {
      return !!this.openCorrections[index]
    },
  },
}
</script>

<style scoped>
.exercise-result-wrap {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.exercise-result-hero {
  padding: 18px;
  border-radius: 18px;
  background: linear-gradient(135deg, rgba(56, 182, 255, 0.10), rgba(115, 103, 240, 0.10));
  border: 1px solid rgba(56, 182, 255, 0.10);
}

.exercise-result-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.result-tag {
  display: inline-flex;
  align-items: center;
  padding: 5px 10px;
  border-radius: 999px;
  background: #ffffff;
  color: #0f172a;
  font-size: 0.75rem;
  font-weight: 800;
}

.result-tag.muted {
  color: #475569;
}

.exercise-result-intro {
  margin: 0;
  color: #1e293b;
  line-height: 1.65;
  font-size: 0.92rem;
}

.exercise-result-list {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.exercise-result-item {
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 18px;
  padding: 20px;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.04);
}

.exercise-result-item-head {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
}

.exercise-result-item-head h5 {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 900;
  color: #0f172a;
}

.exercise-item-index {
  font-size: 0.74rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #0284c7;
}

.exercise-item-context {
  margin: 0 0 14px 0;
  color: #475569;
  font-size: 0.88rem;
  line-height: 1.6;
}

.exercise-item-block + .exercise-item-block {
  margin-top: 16px;
}

.exercise-item-label {
  display: inline-flex;
  align-items: center;
  margin-bottom: 8px;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #64748b;
}

.exercise-item-text {
  margin: 0;
  color: #0f172a;
  line-height: 1.75;
  white-space: pre-line;
  font-size: 0.94rem;
}

.exercise-item-list {
  margin: 0;
  padding-left: 20px;
  color: #0f172a;
  line-height: 1.7;
}

.exercise-item-list li + li {
  margin-top: 8px;
}

.exercise-item-list.ordered {
  padding-left: 22px;
}

.correction-wrapper {
  margin-top: 18px;
}

.correction-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 12px 16px;
  border: 1px solid rgba(40, 199, 111, 0.2);
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(40, 199, 111, 0.04), rgba(40, 199, 111, 0.08));
  color: #15803d;
  font-size: 0.84rem;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
}

.correction-toggle:hover {
  background: linear-gradient(135deg, rgba(40, 199, 111, 0.08), rgba(40, 199, 111, 0.14));
}

.correction-toggle-icon {
  font-size: 0.7rem;
  transition: transform 0.2s ease;
}

.correction-slide-enter-active,
.correction-slide-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.correction-slide-enter,
.correction-slide-leave-to {
  opacity: 0;
  max-height: 0;
}

.correction-body {
  margin-top: 12px;
  padding: 16px;
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(40, 199, 111, 0.06), rgba(40, 199, 111, 0.12));
  border: 1px solid rgba(40, 199, 111, 0.18);
}

.correction-body .exercise-item-text {
  color: #1e293b;
}
</style>
